import ks from "../ReUsableTS/kBrowserShared";
import type { kHTMLMultiSelect } from "./kHTMLMultiSelect";
/**
 * Top-layer (Popover API) promotion of a kHTMLMultiSelect's open panel: feature detection,
 * viewport placement and host reflow tracking. Split out of kHTMLMultiSelect.ts for size;
 * kHTMLMultiSelect keeps same-signature delegating members. Error-handler labels are unchanged.
 */
export class kHTMLMultiSelectTopLayer {
    /** True once the open panel is actually promoted, so close/reflow only act when it is. */
    public InTopLayer: boolean = false;
    private Owner: kHTMLMultiSelect;
    constructor(Owner: kHTMLMultiSelect) {
        this.Owner = Owner;
    }
    /**
     * Can this browser promote the panel to the top layer? Feature-detected, never version-sniffed.
     * A browser without the Popover API keeps the original in-flow panel, which is the behaviour
     * every consumer had before 2026-08-16 - degraded, not broken.
     */
    public CanUseTopLayer = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (this.Owner.UseTopLayer !== true) { return false; }
            if (this.Owner.ListWrapper === null) { return false; }
            const Panel: HTMLElement = this.Owner.ListWrapper;
            if (typeof Panel.showPopover !== "function") { return false; }
            if (typeof Panel.hidePopover !== "function") { return false; }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLMultiSelectTopLayer.CanUseTopLayer");
            return false;
        }
    };
    /**
     * Place the promoted panel under (or over) the control.
     *
     * In the top layer the containing block is the VIEWPORT, so the panel must be positioned from
     * the control's own viewport rect - anchoring it to the offset parent, as the in-flow panel
     * does, would put it at the corner of the screen.
     */
    public PositionPanel = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Owner.ListWrapper === null || this.InTopLayer !== true) { return; }
            const Anchor: DOMRect = this.Owner.OuterDiv.getBoundingClientRect();
            const Panel: HTMLDivElement = this.Owner.ListWrapper;
            Panel.style.position = "fixed";
            Panel.style.margin = "0";
            Panel.style.minWidth = String(Math.round(Anchor.width)) + "px";
            const Height: number = Panel.offsetHeight;
            const Below: number = window.innerHeight - Anchor.bottom;
            // Flip above only when there is genuinely more room there, so the panel does not
            // hang off the bottom of a viewport the host has already filled.
            let Top: number = Anchor.bottom;
            if (Below < Height && Anchor.top > Below) { Top = Anchor.top - Height; }
            // ⚠ CLAMP INTO THE VIEWPORT, and this is not cosmetic. A top-layer panel is FIXED, so
            // unlike the in-flow panel it replaced, it cannot be scrolled to: placed off-screen it
            // is unreachable for ever, by a user and by a test. When the control itself is out of
            // view the panel is pinned to the nearest edge instead - detached from its control, but
            // visible, which is the whole promise of this path. Caught by the suite, not by review:
            // the first picker sits below the fold, and a click on one of its rows timed out with
            // "element is outside of the viewport".
            if (Top + Height > window.innerHeight) { Top = window.innerHeight - Height; }
            if (Top < 0) { Top = 0; }
            let Left: number = Anchor.left;
            const Overflow: number = Left + Panel.offsetWidth - window.innerWidth;
            if (Overflow > 0) { Left = Left - Overflow; }
            if (Left < 0) { Left = 0; }
            Panel.style.top = String(Math.round(Top)) + "px";
            Panel.style.left = String(Math.round(Left)) + "px";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLMultiSelectTopLayer.PositionPanel");
        }
    };
    /**
     * Re-place the panel when the page moves under it. Registered on scroll in CAPTURE mode so a
     * nested scroller counts too - a scroll inside a dialog does not bubble, and that is exactly
     * the host this feature exists for.
     */
    public OnHostReflow = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.PositionPanel();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLMultiSelectTopLayer.OnHostReflow");
        }
    };
    /** Promote the open panel and start tracking the host. No-op when unsupported or opted out. */
    public Show = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.CanUseTopLayer() !== true || this.Owner.ListWrapper === null) { return; }
            const Panel: HTMLElement = this.Owner.ListWrapper;
            if (Panel.isConnected !== true) { return; }
            // showPopover() THROWS if the element is already open, so the flag is the guard.
            if (this.InTopLayer === true) { this.PositionPanel(); return; }
            Panel.setAttribute("popover", "manual");
            Panel.showPopover();
            this.InTopLayer = true;
            this.PositionPanel();
            window.addEventListener("scroll", this.OnHostReflow, true);
            window.addEventListener("resize", this.OnHostReflow);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLMultiSelectTopLayer.Show");
        }
    };
    /** Demote the panel and stop tracking. Safe to call when it was never promoted. */
    public Hide = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Owner.ListWrapper === null || this.InTopLayer !== true) { return; }
            const Panel: HTMLElement = this.Owner.ListWrapper;
            this.InTopLayer = false;
            window.removeEventListener("scroll", this.OnHostReflow, true);
            window.removeEventListener("resize", this.OnHostReflow);
            // hidePopover() THROWS when the element is not open; the attribute check is what keeps
            // a double close (OKClick calls DocumentClick, which also closes) from reporting.
            if (Panel.hasAttribute("popover") === true) { Panel.hidePopover(); }
            Panel.removeAttribute("popover");
            Panel.style.position = "";
            Panel.style.top = "";
            Panel.style.left = "";
            Panel.style.margin = "";
            Panel.style.minWidth = "";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLMultiSelectTopLayer.Hide");
        }
    };
}
